/**
 * Abhaken mit Fingertest. Ablauf:
 *  1. „Wie fühlt sich die Erde an?“ (bei Aufgaben mit Fingertest)
 *  2. Trocken → „Gegossen“ | Feucht → „Noch feucht, später prüfen“
 *  3. Danach: Rückgängig-Leiste + optional Foto fürs Tagebuch
 */
import { useState } from 'react';
import { Camera, Clock, Droplets, Undo2 } from 'lucide-react';
import {
  SOIL_LABEL,
  fmtDueRelative,
  dayKeyDiff,
  localDateKey,
  overwaterWarning,
  previewNextDue,
  shouldWater,
  type Soil,
  type TaskStatus,
} from '@pflanzen/shared';
import { logEvent, undoEvent, getStore } from '../lib/store';
import { uploadPhoto } from '../lib/photos';
import { haptic } from '../lib/haptics';
import { Button, Sheet, cx } from './kit';

const SOILS: { key: Soil; emoji: string; hint: string }[] = [
  { key: 'very_dry', emoji: '🏜️', hint: 'staubig, zieht sich vom Rand weg' },
  { key: 'dry', emoji: '🍂', hint: 'nichts bleibt am Finger' },
  { key: 'slightly_moist', emoji: '💧', hint: 'kühl, leicht krümelig' },
  { key: 'moist', emoji: '💦', hint: 'klebt am Finger' },
];

type Step = 'soil' | 'confirm' | 'done';

export function CheckSheet({ status, onClose }: { status: TaskStatus | null; onClose: () => void }) {
  const [step, setStep] = useState<Step>('soil');
  const [soil, setSoil] = useState<Soil | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);
  const [resultText, setResultText] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoDone, setPhotoDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!status) return null;
  const s = status;
  const tz = getStore().state?.settings.timezone ?? 'Europe/Vienna';
  const ideal: Soil = s.species?.idealSoil ?? 'dry';
  const depth = s.species?.fingerDepthCm ?? 3;
  const needsTest = s.type.fingerTest;

  const close = () => {
    setStep('soil');
    setSoil(null);
    setEventId(null);
    setPhotoDone(false);
    setError(null);
    onClose();
  };

  const nextText = (outcome: 'done' | 'still_moist' | 'skipped') => {
    const key = previewNextDue(s, outcome, new Date(), tz);
    return fmtDueRelative(dayKeyDiff(localDateKey(new Date(), tz), key));
  };

  const commit = async (outcome: 'done' | 'still_moist' | 'skipped' | 'snoozed', extra: { snoozeUntil?: string } = {}) => {
    haptic();
    const id = await logEvent({ taskId: s.task.id, outcome, soil, snoozeUntil: extra.snoozeUntil ?? null });
    setEventId(id);
    if (outcome === 'done') setResultText(`${s.type.doneLabel} ✓ · nächste Prüfung ${nextText('done')}`);
    else if (outcome === 'still_moist') setResultText(`Noch feucht – gut, dass du geprüft hast. Nächster Blick ${nextText('still_moist')}.`);
    else if (outcome === 'snoozed') setResultText('Ich erinnere dich nochmal.');
    else setResultText('Ausgelassen.');
    setStep('done');
  };

  const pickSoil = (value: Soil) => {
    haptic();
    setSoil(value);
    setStep('confirm');
  };

  const snooze = (hours: number) => {
    const until = new Date(Date.now() + hours * 3_600_000).toISOString();
    void commit('snoozed', { snoozeUntil: until });
  };

  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    setPhotoBusy(true);
    setError(null);
    try {
      await uploadPhoto(s.plant.id, file, { eventId });
      setPhotoDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Foto konnte nicht gespeichert werden.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const warning = overwaterWarning(s);
  const wouldWater = soil ? shouldWater(soil, ideal) : true;
  const title = `${s.plant.emoji ?? s.type.icon} ${s.plant.name}`;

  return (
    <Sheet open onClose={close} title={title}>
      {step === 'soil' && needsTest && (
        <div className="rise">
          <p className="mb-1 text-[15px]">
            <b>Fingertest:</b> Finger ca. {depth} cm tief in die Erde. Wie fühlt sie sich an?
          </p>
          <p className="mb-4 text-sm text-muted">Gießen nur, wenn sie {SOIL_LABEL[ideal].toLowerCase()} oder trockener ist.</p>
          <div className="grid grid-cols-2 gap-2.5">
            {SOILS.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => pickSoil(o.key)}
                className="flex min-h-24 flex-col items-start justify-start gap-1.5 rounded-2xl border border-line bg-card p-3.5 text-left active:scale-[0.97]"
              >
                <span className="text-2xl leading-none">{o.emoji}</span>
                <span>
                  <span className="block text-[15px] font-semibold">{SOIL_LABEL[o.key]}</span>
                  <span className="block text-xs text-muted">{o.hint}</span>
                </span>
              </button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2.5">
            <Button tone="ghost" onClick={() => snooze(4)}>
              <Clock className="mr-1.5 inline h-4 w-4" />
              Später (4 h)
            </Button>
            <Button tone="ghost" onClick={() => void commit('skipped')}>
              Auslassen
            </Button>
          </div>
        </div>
      )}

      {(step === 'confirm' || (step === 'soil' && !needsTest)) && (
        <div className="rise">
          {soil && (
            <p className="mb-3 text-[15px]">
              Erde: <b>{SOIL_LABEL[soil]}</b>.{' '}
              {wouldWater ? 'Zeit zum Gießen.' : 'Lieber noch warten – zu viel Wasser schadet mehr als zu wenig.'}
            </p>
          )}
          {!needsTest && <p className="mb-3 text-[15px]">{s.type.description || s.type.action}</p>}
          {warning && wouldWater && soil !== 'very_dry' && soil !== 'dry' && (
            <p className="mb-3 rounded-2xl bg-sun-soft p-3 text-sm">⚠️ {warning}</p>
          )}
          <div className="grid gap-2.5">
            <Button tone={wouldWater ? 'water' : 'ghost'} onClick={() => void commit('done')}>
              <Droplets className="mr-1.5 inline h-4 w-4" />
              {s.type.doneLabel}
            </Button>
            {needsTest && (
              <Button tone={wouldWater ? 'ghost' : 'moss'} onClick={() => void commit('still_moist')}>
                Noch feucht – später prüfen ({nextText('still_moist')})
              </Button>
            )}
            <button type="button" className="py-2 text-sm text-muted" onClick={() => setStep('soil')}>
              ← zurück
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="rise">
          <div className="pop mb-4 rounded-2xl bg-moss-soft p-4 text-[15px] text-moss">{resultText}</div>
          <label className={cx('mb-2.5 flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-line bg-transparent px-4 text-[15px] font-semibold active:scale-[0.97]', photoBusy && 'opacity-50')}>
            <Camera className="h-4 w-4" />
            {photoDone ? 'Foto gespeichert ✓' : photoBusy ? 'Speichere Foto …' : 'Foto fürs Tagebuch'}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              disabled={photoBusy || photoDone}
              onChange={(e) => void onPhoto(e.target.files?.[0])}
            />
          </label>
          {error && <p className="mb-2 text-sm text-alert">{error}</p>}
          <div className="grid grid-cols-2 gap-2.5">
            <Button
              tone="ghost"
              onClick={() => {
                if (eventId) void undoEvent(eventId);
                close();
              }}
            >
              <Undo2 className="mr-1.5 inline h-4 w-4" />
              Rückgängig
            </Button>
            <Button onClick={close}>Fertig</Button>
          </div>
        </div>
      )}
    </Sheet>
  );
}
